import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_POS_CATEGORIES,
  getCurrentStore,
  updateCurrentStore,
  type PosCategoryTab,
} from '@/lib/api/stores.js';
import { listInventoryItems } from '@/lib/api/inventory.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatCurrency } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

const CATEGORY_OPTIONS: Array<{ id: PosCategoryTab; label: string }> = [
  { id: 'repairs', label: 'Repairs (device drill-down)' },
  { id: 'accessories', label: 'Accessories' },
  { id: 'unlocking', label: 'Unlocking' },
  { id: 'trade_in', label: 'Trade-In' },
  { id: 'misc', label: 'Miscellaneous' },
];

const DEFAULT_TABS: Array<PosCategoryTab | 'all'> = [
  'all',
  ...DEFAULT_POS_CATEGORIES,
];

export function PosSettingsPage() {
  const queryClient = useQueryClient();
  const storeQuery = useQuery({ queryKey: ['current-store'], queryFn: getCurrentStore });

  const [enabled, setEnabled] = useState<PosCategoryTab[]>(DEFAULT_POS_CATEGORIES);
  const [showOutOfStock, setShowOutOfStock] = useState(true);
  const [requireCustomer, setRequireCustomer] = useState(false);
  const [defaultCategory, setDefaultCategory] = useState<PosCategoryTab | 'all'>('all');
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  const [pinSearch, setPinSearch] = useState('');

  useEffect(() => {
    const store = storeQuery.data;
    if (!store) return;
    setEnabled((store.posCategoriesEnabled as PosCategoryTab[]) ?? DEFAULT_POS_CATEGORIES);
    setShowOutOfStock(store.posShowOutOfStock !== false);
    setRequireCustomer(store.posRequireCustomer === true);
    setDefaultCategory((store.posDefaultCategory as PosCategoryTab | 'all') ?? 'all');
    setPinnedIds(store.posPinnedItems ?? []);
  }, [storeQuery.data]);

  const pinSearchQuery = useQuery({
    queryKey: ['pos-settings-pin-search', pinSearch],
    queryFn: () => listInventoryItems({ search: pinSearch, page: 1, pageSize: 8 }),
    enabled: pinSearch.trim().length >= 2,
  });

  const pinnedItemsQuery = useQuery({
    queryKey: ['pos-settings-pinned', pinnedIds.join(',')],
    queryFn: async () => {
      if (!pinnedIds.length) return [];
      const res = await listInventoryItems({ page: 1, pageSize: 50 });
      return res.data.filter((item) => pinnedIds.includes(item.id));
    },
    enabled: pinnedIds.length > 0,
  });

  const pinnedRows = useMemo(() => {
    const map = new Map((pinnedItemsQuery.data ?? []).map((item) => [item.id, item]));
    return pinnedIds.map((id) => map.get(id)).filter(Boolean);
  }, [pinnedIds, pinnedItemsQuery.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      updateCurrentStore({
        posCategoriesEnabled: enabled,
        posShowOutOfStock: showOutOfStock,
        posRequireCustomer: requireCustomer,
        posDefaultCategory: defaultCategory,
        posPinnedItems: pinnedIds,
      }),
    onSuccess: () => {
      toastSuccess('POS settings saved');
      queryClient.invalidateQueries({ queryKey: ['current-store'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function toggleCategory(id: PosCategoryTab) {
    setEnabled((current) =>
      current.includes(id) ? current.filter((c) => c !== id) : [...current, id],
    );
  }

  function addPinned(id: string) {
    if (pinnedIds.includes(id) || pinnedIds.length >= 12) return;
    setPinnedIds((current) => [...current, id]);
    setPinSearch('');
  }

  function removePinned(id: string) {
    setPinnedIds((current) => current.filter((row) => row !== id));
  }

  function movePinned(id: string, direction: -1 | 1) {
    setPinnedIds((current) => {
      const index = current.indexOf(id);
      if (index < 0) return current;
      const next = index + direction;
      if (next < 0 || next >= current.length) return current;
      const copy = [...current];
      const [row] = copy.splice(index, 1);
      copy.splice(next, 0, row!);
      return copy;
    });
  }

  if (storeQuery.isLoading) {
    return <p className="text-slate-500">Loading…</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-950">POS Settings</h1>
        <p className="mt-1 text-sm text-slate-600">
          Configure categories, pinned items, and checkout behavior for the point of sale screen.
        </p>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="font-bold text-slate-950">Display Settings</h2>
        <div className="mt-4 space-y-3">
          <p className="text-sm font-semibold text-slate-700">Categories visible in POS</p>
          {CATEGORY_OPTIONS.map((opt) => (
            <label key={opt.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={enabled.includes(opt.id)}
                onChange={() => toggleCategory(opt.id)}
              />
              {opt.label}
            </label>
          ))}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showOutOfStock}
              onChange={(e) => setShowOutOfStock(e.target.checked)}
            />
            Show out-of-stock items (with badge)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={requireCustomer}
              onChange={(e) => setRequireCustomer(e.target.checked)}
            />
            Require customer for checkout
          </label>
          <div>
            <label className="text-sm font-semibold text-slate-700">Default category on open</label>
            <select
              className="mt-1 w-full max-w-xs rounded-xl border border-slate-300 px-3 py-2 text-sm"
              value={defaultCategory}
              onChange={(e) => setDefaultCategory(e.target.value as PosCategoryTab | 'all')}
            >
              {DEFAULT_TABS.filter((tab) => tab === 'all' || enabled.includes(tab)).map((tab) => (
                <option key={tab} value={tab}>
                  {tab === 'all' ? 'All' : tab.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="font-bold text-slate-950">Pinned Items</h2>
        <p className="mt-1 text-sm text-slate-600">
          These items appear as quick-tap tiles in the All tab. Max 12 items.
        </p>
        <div className="relative mt-4">
          <input
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            placeholder="Search inventory items to pin..."
            value={pinSearch}
            onChange={(e) => setPinSearch(e.target.value)}
          />
          {pinSearchQuery.data?.data.length ? (
            <div className="absolute z-10 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg">
              {pinSearchQuery.data.data.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="flex w-full justify-between px-4 py-2 text-left text-sm hover:bg-slate-50"
                  onClick={() => addPinned(item.id)}
                >
                  <span>{item.name}</span>
                  <span>{formatCurrency(item.retailPrice)}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <ul className="mt-4 space-y-2">
          {pinnedRows.map((item, index) => (
            <li
              key={item!.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <span className="text-slate-400">⠿</span>
                <span className="text-sm font-semibold">{item!.name}</span>
                <span className="text-sm text-slate-500">{formatCurrency(item!.retailPrice)}</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() => movePinned(item!.id, -1)}
                  className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                >
                  ↑
                </button>
                <button
                  type="button"
                  disabled={index === pinnedRows.length - 1}
                  onClick={() => movePinned(item!.id, 1)}
                  className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => removePinned(item!.id)}
                  className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                >
                  ×
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <button
        type="button"
        disabled={saveMut.isPending}
        onClick={() => saveMut.mutate()}
        className="rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50"
      >
        {saveMut.isPending ? 'Saving…' : 'Save POS Settings'}
      </button>
    </div>
  );
}
